/**
 * API クライアント
 *
 * Cognito JWT 自動付与、リトライ (5xx/429)、タイムアウト、
 * 401 トークンリフレッシュ、403 CustomEvent dispatch を提供する。
 */
import { fetchAuthSession } from 'aws-amplify/auth';

const API_BASE = import.meta.env.DEV
  ? '/api'
  : import.meta.env.VITE_API_URL || 'https://api.ace-collect.com';

// リトライ設定
const MAX_RETRIES = 2;
const RETRY_DELAYS = [1000, 2000]; // ms (exponential backoff)
const REQUEST_TIMEOUT = 30_000; // 30 秒

/** リトライ対象のステータスコードか判定 */
function isRetryable(status: number): boolean {
  return status >= 500 || status === 429;
}

/** 指定ミリ秒だけ待機 */
function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Cognito からトークンを取得する
 * DEV/PROD 共通で実 JWT を取得
 */
async function getToken(forceRefresh = false): Promise<string> {
  try {
    const session = await fetchAuthSession({ forceRefresh });
    return session.tokens?.idToken?.toString() ?? '';
  } catch {
    return '';
  }
}

/**
 * API レスポンスエラー
 *
 * **失敗の理由 (見つからない / 競合 / 権限 / 入力) は `code` で分ける。** BE は `shared/response.ts` の
 * `{ statusCode, code, message }` を返し、`code` が業務上の意味の正本 (NOT_FOUND / CONFLICT / 個別の安定 code)。
 * `status` は HTTP status で、**通信の一時障害 (429 / 5xx) の判定にだけ使う** — API Gateway が返す 429 / 502 /
 * 504 などは body に `code` が無いため、再試行してよいかは status でしか分からない (bugfix-sweep-20260924)。
 */
export class ApiResponseError extends Error {
  /** backend が返す safe error code (例: CHATWORK_AUTH_FAILED)。無い場合は undefined。 */
  code?: string;
  /** backend が回復に必要な最小情報として返す `details` (PII なし、steering 565 §16.4(a))。無い場合は undefined。 */
  details?: Record<string, unknown>;
  /**
   * HTTP status (apiFetch が応答から設定する)。test などで直接作った error では undefined。
   * 業務の理由の出し分けには使わず `code` を使う (上の説明)。
   */
  status?: number;
  constructor(message: string, code?: string, details?: Record<string, unknown>, status?: number) {
    super(message);
    this.name = 'ApiResponseError';
    this.code = code;
    this.details = details;
    this.status = status;
  }
}

/**
 * apiFetch が投げたエラーが 403 (権限不足) かどうかを判定する。
 *
 * 403 は client が api:forbidden CustomEvent 経由でグローバルトーストを表示するため、
 * 個別の mutation / fetch 呼び出し側で再度トーストを出すと二重表示になる。
 * 呼び出し側はこの helper で 403 を判定し、自前のトーストを抑止する。
 */
export function isForbiddenApiError(err: unknown): boolean {
  return err instanceof ApiResponseError && err.code === 'FORBIDDEN';
}

/** 冪等 method (自動リトライしても副作用が増えない) */
const IDEMPOTENT_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

/**
 * apiFetch の拡張オプション。
 *
 * `retryNonIdempotent`: 非冪等 method (POST/PUT/PATCH/DELETE) の自動リトライを**明示的に**
 * 有効化する。**サーバ側 idempotency が保証されている endpoint でのみ true にすること。**
 * 既定は false — 自動リトライしない。
 */
export interface ApiFetchInit extends RequestInit {
  retryNonIdempotent?: boolean;
  /** OAuth callback は秘密値を破棄した画面で再ログイン・再接続を案内する。既定は従来どおり遷移。 */
  redirectOnSessionExpired?: boolean;
}

/**
 * 自動リトライしてよいリクエストか判定する。
 *
 * 2026-08-18 incident の再発防止: 旧実装は **method を見ずに** 5xx/429/network error を
 * 最大 2 回リトライしていた。外部送信を伴う POST (メール送信・リマインド等) で 5xx が返ると、
 * サーバは処理済みなのにクライアントが再送し、**外部送信・通知・監査ログが多重化**する。
 * 冪等 method 以外は、明示 opt-in (`retryNonIdempotent`) が無い限りリトライしない。
 */
function mayAutoRetry(method: string | undefined, optIn: boolean | undefined): boolean {
  if (optIn === true) return true;
  return IDEMPOTENT_METHODS.has((method ?? 'GET').toUpperCase());
}

/**
 * API リクエストを送信する汎用関数
 *
 * - Cognito JWT を自動付与
 * - 5xx/429 は最大 2 回リトライ (1s, 2s exponential backoff)。
 *   **リトライ対象は冪等 method (GET/HEAD/OPTIONS) のみ。** 非冪等 method は
 *   `retryNonIdempotent: true` を明示した場合だけリトライする (server-side idempotency 必須)
 * - 30 秒タイムアウト (AbortController)
 * - 401: トークンリフレッシュ (1 回限り)、失敗時は signOut + /login リダイレクト
 * - 403: POST/PUT/DELETE のみ api:forbidden CustomEvent を dispatch
 * - X-Client-Id ヘッダーを localStorage から取得して付与
 */
export async function apiFetch<T>(path: string, init?: ApiFetchInit): Promise<T> {
  const {
    headers: initHeaders,
    signal: initSignal,
    retryNonIdempotent,
    redirectOnSessionExpired,
    ...restInit
  } = init ?? {};
  const autoRetryAllowed = mayAutoRetry(restInit.method, retryNonIdempotent);
  let token = await getToken();
  let lastError: Error | null = null;
  let refreshAttempted = false;

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    // ヘッダー構築
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
    const clientId = localStorage.getItem('acecollect_client_id');
    if (clientId) headers['X-Client-Id'] = clientId;
    const mergedHeaders = { ...headers, ...(initHeaders as Record<string, string>) };

    // タイムアウト制御
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);
    if (initSignal) {
      if (initSignal.aborted) {
        clearTimeout(timeoutId);
        throw new Error('リクエストが中止されました');
      }
      initSignal.addEventListener('abort', () => controller.abort(), { once: true });
    }

    try {
      const res = await fetch(`${API_BASE}${path}`, {
        ...restInit,
        headers: mergedHeaders,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      // 401: トークンリフレッシュ試行 (1 回限り)
      if (res.status === 401 && !refreshAttempted) {
        refreshAttempted = true;
        const newToken = await getToken(true);
        if (newToken) {
          token = newToken;
          continue;
        }
        try {
          const { signOut } = await import('aws-amplify/auth');
          await signOut();
        } catch {
          // signOut 失敗時もリダイレクトは行う
        }
        if (redirectOnSessionExpired === false) {
          throw new ApiResponseError(
            'セッションが期限切れです。再ログインしてください。',
            'UNAUTHORIZED',
            undefined,
            401,
          );
        }
        window.location.href = '/login';
        throw new Error('セッションが期限切れです。再ログインしてください。');
      }

      // 5xx/429: リトライ (冪等 method、または明示 opt-in のときだけ)
      if (autoRetryAllowed && isRetryable(res.status) && attempt < MAX_RETRIES) {
        await sleep(RETRY_DELAYS[attempt]);
        continue;
      }

      // 403: 権限不足 → CustomEvent dispatch (POST/PUT/DELETE のみ)
      if (res.status === 403) {
        const body = (await res.json().catch(() => ({}))) as { message?: string; code?: string };
        const message = body.message || 'この操作を行う権限がありません';
        const method = (restInit.method ?? 'GET').toUpperCase();
        if (method !== 'GET') {
          window.dispatchEvent(new CustomEvent('api:forbidden', { detail: { path, message } }));
        }
        // 403 は確定エラー。ApiResponseError として throw し retry ループに乗せない。
        // (旧実装は plain Error を投げており、下の catch 節の terminal 判定
        //  (instanceof ApiResponseError) に当たらず最大 MAX_RETRIES 回 再送していた。
        //  その結果 api:forbidden が複数回 dispatch されトーストが重複表示されていた)
        throw new ApiResponseError(message, body.code || 'FORBIDDEN', undefined, res.status);
      }

      // 4xx: リトライなし → エラー (#298: backend の safe code も surface する)
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as {
          message?: string;
          code?: string;
          details?: Record<string, unknown>;
        };
        throw new ApiResponseError(
          body.message || `API Error: ${res.status}`,
          body.code,
          body.details && typeof body.details === 'object' ? body.details : undefined,
          res.status,
        );
      }

      // 204 No Content や空レスポンスは JSON パースしない
      if (res.status === 204 || res.headers.get('content-length') === '0') {
        return {} as T;
      }
      const text = await res.text();
      if (!text) return {} as T;
      return JSON.parse(text) as T;
    } catch (err) {
      clearTimeout(timeoutId);

      // AbortError (タイムアウト)
      if (err instanceof DOMException && err.name === 'AbortError') {
        lastError = new Error(`リクエストがタイムアウトしました (${REQUEST_TIMEOUT / 1000}秒)`);
        break;
      }

      // セッション期限切れは即座にスロー
      if (err instanceof Error && err.message.includes('セッション')) throw err;

      // ApiResponseError はリトライしない
      if (err instanceof ApiResponseError) throw err;

      // network error 等。**非冪等 method はここでもリトライしない** — サーバが処理済みでも
      // レスポンスだけ失われるケースがあり、再送すると外部送信が二重に走る。
      lastError = err instanceof Error ? err : new Error(String(err));
      if (autoRetryAllowed && attempt < MAX_RETRIES) {
        await sleep(RETRY_DELAYS[attempt]);
        continue;
      }
      if (!autoRetryAllowed) break;
    }
  }

  throw lastError ?? new Error('API request failed');
}
